import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createCompanySchema, messages } from '@nesc/shared';
import { ApiError, api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

/** 企業の手動追加 */
export const CompanyCreateDialog = ({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    websiteUrl: '',
    industry: '',
    prefecture: '',
    city: '',
    addressLine: '',
    phone: '',
    email: '',
    formUrl: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post<SingleResponse<{ id: string; name: string }>>('/companies', payload),
    onSuccess: (result) => {
      toast.success(`「${result.data.name}」を追加しました`);
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      onOpenChange(false);
      setForm({
        name: '',
        websiteUrl: '',
        industry: '',
        prefecture: '',
        city: '',
        addressLine: '',
        phone: '',
        email: '',
        formUrl: '',
      });
      onCreated?.(result.data.id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        // 重複は既存企業を案内する
        if (error.code === 'DUPLICATE_COMPANY') {
          toast.error(error.message);
          return;
        }
      }
      toast.error(errorMessage(error));
    },
  });

  const submit = () => {
    setErrors({});
    const cleaned = Object.fromEntries(
      Object.entries(form).filter(([, value]) => value.trim() !== ''),
    );
    const parsed = createCompanySchema.safeParse(cleaned);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) fieldErrors[issue.path.join('.')] = issue.message;
      setErrors(fieldErrors);
      return;
    }
    mutation.mutate(parsed.data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.company.add}</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <Field label="企業名" required error={errors.name}>
            {(props) => (
              <Input {...props} value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />
            )}
          </Field>
          <Field
            label="URL"
            error={errors.websiteUrl}
            hint="ドメインが同じ企業がすでにある場合は追加できません"
          >
            {(props) => (
              <Input
                {...props}
                type="url"
                inputMode="url"
                value={form.websiteUrl}
                onChange={(e) => set('websiteUrl', e.target.value)}
                placeholder="https://example.co.jp"
              />
            )}
          </Field>
          <Field label="業種" error={errors.industry}>
            {(props) => (
              <Input {...props} value={form.industry} onChange={(e) => set('industry', e.target.value)} />
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="都道府県" error={errors.prefecture}>
              {(props) => (
                <Input {...props} value={form.prefecture} onChange={(e) => set('prefecture', e.target.value)} />
              )}
            </Field>
            <Field label="市区町村" error={errors.city}>
              {(props) => <Input {...props} value={form.city} onChange={(e) => set('city', e.target.value)} />}
            </Field>
          </div>
          <Field label="住所" error={errors.addressLine}>
            {(props) => (
              <Input {...props} value={form.addressLine} onChange={(e) => set('addressLine', e.target.value)} />
            )}
          </Field>
          <Field label="電話番号" error={errors.phone}>
            {(props) => (
              <Input
                {...props}
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="03-1234-5678"
              />
            )}
          </Field>
          <Field label="メールアドレス" error={errors.email}>
            {(props) => (
              <Input
                {...props}
                type="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
              />
            )}
          </Field>
          <Field label="問い合わせフォーム URL" error={errors.formUrl}>
            {(props) => (
              <Input {...props} type="url" value={form.formUrl} onChange={(e) => set('formUrl', e.target.value)} />
            )}
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button size="lg" onClick={submit} disabled={mutation.isPending}>
            {messages.common.add}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
